/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { useEffect, useState, type ChangeEvent } from 'react';
import { Box, Typography, useTheme, Button, TextField } from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  getCustomerOnBoardRequestById,
  rejectOnBoardingRequest,
  approveOnBoardingRequest,
  rejectAvcRequest,
  approveAvcRequest,
} from 'service/CustomerService';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { colorTheme } from 'theme';
import Avatar from '@mui/material/Avatar';
import {
  type ICustomerOnBoradResponse,
  type ICustomerOnBoardRequestApiResponse,
  type IReuseGridProps,
} from 'types';
import { dateFormat } from 'utils/GeneralUtil';
import type { RootState } from 'store/rootReducer';
import { useSelector, useDispatch } from 'react-redux';
import DocumentList from 'components/documentList/DocumentList';
import NoteList from 'components/noteList/NoteList';
import { setSnackBarSuccess } from 'store/snackBar';

import { AlertModal, DialogBox } from 'components';
import { strings } from 'i18n/Strings';
import { getResource } from 'service/UserService';

interface Category {
  id: number;
  categoryName: string;
  parentId: number | null;
  parent_id: number | null;
  subCategories?: Category[];
}

const UserDetails = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [customerInfo, setCustomerInfo] = useState<ICustomerOnBoradResponse>();
  const [image, setImage] = useState('');
  const [isOnboardRejectOpen, setIsOnboardRejectOpen] = useState(false);
  const [onboardRejectNote, setOnboardRejectNote] = useState('');
  const [isOnboardApproveOpen, setIsOnboardApproveOpen] = useState(false);
  const [disableOnboardRejectButton, setDisableOnboardRejectButton] =
    useState(false);
  const [disableOnboardApproveButton, setDisableOnboardApproveButton] =
    useState(false);

  const [isAvcRejectOpen, setIsAvcRejectOpen] = useState(false);
  const [avcRejectNote, setAvcRejectNote] = useState('');
  const [isAvcApproveOpen, setIsAvcApproveOpen] = useState(false);
  const [disableAvcRejectButton, setDisableAvcRejectButton] = useState(false);
  const [disableAvcApproveButton, setDisableAvcApproveButton] = useState(false);

  const { userId } = useParams();
  const { economicStatusAndOccupation } = useSelector(
    (state: RootState) => state.employeeDetails,
  );
  const getCustomerData = async (): Promise<void> => {
    try {
      const response = (await getCustomerOnBoardRequestById(
        Number(userId),
      )) as ICustomerOnBoardRequestApiResponse;
      const details = response.userDetails ?? response;
      setCustomerInfo(details);
      if (details?.profileImage) {
        const profilePicSignedUrl = await getResource(details.profileImage);
        if (profilePicSignedUrl.url) setImage(profilePicSignedUrl.url);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (userId) {
      void getCustomerData();
    }
  }, []);

  const findObjectById = (arr: Category[], id: number): Category | null => {
    if (arr) {
      for (const obj of arr) {
        if (obj.id === id) {
          return obj;
        }
        if (obj.subCategories && obj.subCategories.length > 0) {
          const result = findObjectById(obj.subCategories, id);
          if (result) {
            return result;
          }
        }
      }
      return null;
    }
    return null;
  };

  const handleCloseOnboardReject = (): void => {
    setIsOnboardRejectOpen(false);
    setOnboardRejectNote('');
  };

  const handleOnboardReject = async (): Promise<void> => {
    try {
      setDisableOnboardRejectButton(true);
      await rejectOnBoardingRequest(Number(userId), onboardRejectNote);
      void getCustomerData();
    } catch (error) {
      console.log(error);
    } finally {
      setDisableOnboardRejectButton(false);
      handleCloseOnboardReject();
    }
  };

  const handleOnboardApprove = async (): Promise<void> => {
    if (!customerInfo?.email) {
      return;
    }
    try {
      setDisableOnboardApproveButton(true);
      await approveOnBoardingRequest(Number(userId), customerInfo.email);
      void getCustomerData();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.onBoardApprovedSuccessText,
        }),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setDisableOnboardApproveButton(false);
      setIsOnboardApproveOpen(false);
    }
  };

  const handleCloseAvcReject = (): void => {
    setIsAvcRejectOpen(false);
    setAvcRejectNote('');
  };

  const handleAvcReject = async (): Promise<void> => {
    try {
      setDisableAvcRejectButton(true);
      await rejectAvcRequest(Number(userId), avcRejectNote);
      void getCustomerData();
    } catch (error) {
      console.log(error);
    } finally {
      setDisableAvcRejectButton(false);
      handleCloseAvcReject();
    }
  };

  const handleAvcApprove = async (): Promise<void> => {
    if (!customerInfo?.email) {
      return;
    }
    try {
      setDisableAvcApproveButton(true);
      await approveAvcRequest(Number(userId), customerInfo.email);
      void getCustomerData();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.avcApprovedSuccessText,
        }),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setDisableAvcApproveButton(false);
      setIsAvcApproveOpen(false);
    }
  };

  const ReuseGrid = (props: IReuseGridProps): JSX.Element => (
    <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', padding: 1 }}>
      <Grid item xs={4} sm={4} md={4} paddingLeft={1}>
        <Typography
          component="h5"
          variant="h5"
          color="#838383"
          fontWeight={700}
          fontSize={16}
          fontFamily="Inter !important"
        >
          {props.customName}
        </Typography>
      </Grid>
      <Grid item xs={4} sm={4} md={4}>
        <Typography>:</Typography>
      </Grid>
      <Grid item xs={4} sm={4} md={4}>
        <Typography
          component="h5"
          variant="h5"
          color={colors.gray[100]}
          fontWeight={700}
          fontSize={16}
          fontFamily="Inter !important"
        >
          {props.customerDetails}
        </Typography>
      </Grid>
    </Grid>
  );

  const onboardingDocs =
    customerInfo?.onboardingDocumentImages !== undefined
      ? customerInfo.onboardingDocumentImages
      : customerInfo?.document ?? [];
  const fundingDocs = customerInfo?.fundingLimitCheckDocumentImages ?? [];
  const onboardingNotesList =
    customerInfo?.onboardingNotes !== undefined
      ? customerInfo.onboardingNotes
      : customerInfo?.notes?.filter(
          (n) =>
            n.type !== 'FundingLimitCheck' &&
            String(n.type ?? '').toLowerCase() !== 'fundinglimitcheck',
        ) ?? [];
  const fundingNotesList =
    customerInfo?.fundingLimitCheckNotes !== undefined
      ? customerInfo.fundingLimitCheckNotes
      : customerInfo?.notes?.filter(
          (n) =>
            n.type === 'FundingLimitCheck' ||
            String(n.type ?? '').toLowerCase() === 'fundinglimitcheck',
        ) ?? [];

  return (
    <Box>
      <Paper
        sx={{
          margin: 'auto',

          flexGrow: 1,
        }}
      >
        <Grid container item xs={12} sm={12} md={12}>
          <Grid item xs={3} sm={3} md={3}>
            <Box
              sx={{
                padding: 2,
              }}
            >
              <Typography
                component="h5"
                variant="h5"
                color={colors.greenAccent[500]}
                fontWeight={800}
                fontSize={18}
                fontFamily="Inter !important"
                sx={{
                  textAlign: 'center',
                  marginBlock: 2,
                }}
              >
                Profile picture
              </Typography>
              <Box marginBottom={5}></Box>
              <Box
                sx={{
                  alignSelf: 'center',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                {customerInfo?.profileImage ? (
                  <Avatar
                    sx={{
                      height: '100px',
                      width: '100px',
                      alignSelf: 'center',
                      bgcolor: '#e4dfed',
                      color: '#202020',
                      fontWeight: 700,
                      fontSize: 40,
                    }}
                    alt="img"
                    src={image}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: '100px',
                      height: '100px',
                      alignSelf: 'center',
                      bgcolor: '#B5DFC8',
                      color: '#202020',
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: 40,
                    }}
                  >
                    {customerInfo?.firstName &&
                      customerInfo?.firstName.slice(0, 1) +
                        customerInfo?.lastName.slice(0, 1)}
                  </Avatar>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={8} sm={8} md={8} sx={{ marginBlock: 2 }}>
            <Box marginTop={1}></Box>
            <ReuseGrid
              customName={strings.displayText.fullName}
              customerDetails={
                customerInfo?.firstName
                  ? `${customerInfo?.firstName} ${customerInfo?.lastName}`
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.emailAddress}
              customerDetails={customerInfo?.email ? customerInfo?.email : '-'}
            />
            <ReuseGrid
              customName={strings.displayText.phoneNumber}
              customerDetails={
                customerInfo?.phone
                  ? `${customerInfo?.callingCode?.startsWith('+') ? '' : '+'}${
                      customerInfo?.callingCode
                    } ${customerInfo?.phone}`
                  : '-'
              }
            />
            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName={strings.displayText.countryOfBirth}
              customerDetails={
                customerInfo?.country ? customerInfo?.country : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.citizenship}
              customerDetails={
                customerInfo?.citizenship ? customerInfo?.citizenship : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.dateOfBirth}
              customerDetails={
                customerInfo?.dob ? dateFormat(customerInfo?.dob) : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.gender}
              customerDetails={
                customerInfo?.gender
                  ? (() => {
                      const genderStr = String(customerInfo.gender);
                      return `${genderStr.charAt(0).toUpperCase()}${genderStr
                        .slice(1)
                        .toLowerCase()}`;
                    })()
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.maritalStatus}
              customerDetails={
                customerInfo?.maritalStatus ? customerInfo.maritalStatus : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.address}
              customerDetails={
                customerInfo?.address ? customerInfo?.address : '-'
              }
            />

            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName={strings.displayText.employeeContribution}
              customerDetails={
                customerInfo?.employeeContribution != null &&
                customerInfo?.employeeContribution !== ''
                  ? customerInfo.employeeContribution
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.employerContribution}
              customerDetails={
                customerInfo?.employerContribution != null &&
                customerInfo?.employerContribution !== ''
                  ? customerInfo.employerContribution
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.employeeContributionPercentage}
              customerDetails={
                customerInfo?.employeeContributionPercentage != null &&
                customerInfo?.employeeContributionPercentage !== ''
                  ? customerInfo.employeeContributionPercentage
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.employerContributionPercentage}
              customerDetails={
                customerInfo?.employerContributionPercentage != null &&
                customerInfo?.employerContributionPercentage !== ''
                  ? customerInfo.employerContributionPercentage
                  : '-'
              }
            />

            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName={strings.displayText.economicStatus}
              customerDetails={
                customerInfo?.economicStatus
                  ? economicStatusAndOccupation?.categories?.find(
                      (val) =>
                        val.id ===
                        findObjectById(
                          economicStatusAndOccupation?.categories,
                          customerInfo.economicStatus,
                        )?.parentId,
                    )?.categoryName ?? '-'
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.economicOccupation}
              customerDetails={
                customerInfo?.economicStatus
                  ? findObjectById(
                      economicStatusAndOccupation?.categories,
                      customerInfo.economicStatus,
                    )?.categoryName ?? '-'
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.ppsn}
              customerDetails={
                customerInfo?.ppsnNumber ? customerInfo?.ppsnNumber : '-'
              }
            />

            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName="AVC Details"
              customerDetails={
                customerInfo?.isAvcPensionUser === null
                  ? '-'
                  : customerInfo?.isAvcPensionUser
                  ? 'Yes'
                  : 'No'
              }
            />
            <ReuseGrid
              customName="Onboarding approval status"
              customerDetails={
                customerInfo?.approvalStatus ? customerInfo.approvalStatus : '-'
              }
            />
            <ReuseGrid
              customName="AVC approval status"
              customerDetails={
                customerInfo?.avcApprovalStatus
                  ? customerInfo.avcApprovalStatus
                  : '-'
              }
            />
            <Divider sx={{ marginBlock: 2 }} />
            <Typography
              component="h2"
              variant="h6"
              sx={{
                color: colors.greenAccent[500],
                fontWeight: 700,
                fontFamily: 'Inter !important',
                marginTop: 1,
              }}
            >
              {strings.displayText.onboardingSectionTitle}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}
            >
              {strings.displayText.DocumentsTab}
            </Typography>
            {onboardingDocs.length > 0 ? (
              <DocumentList documents={onboardingDocs} hideTitle />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {strings.displayText.noDocumentsUploaded}
              </Typography>
            )}
            {onboardingNotesList.length > 0 ? (
              <NoteList
                notes={onboardingNotesList}
                heading={strings.displayText.sectionNotesHeading}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {strings.displayText.noNotesYet}
              </Typography>
            )}

            {customerInfo && customerInfo.approvalStatus !== 'Approved' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 16,
                }}
              >
                <Button
                  variant="contained"
                  style={{ margin: '0px 5px' }}
                  sx={{
                    height: 40,
                    width: 90,
                    bgcolor: colors.greenAccent[500],
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                  onClick={() => {
                    setIsOnboardRejectOpen(true);
                  }}
                >
                  Reject
                </Button>
                <Button
                  disabled={
                    customerInfo?.approvalStatus === 'Rejected' ||
                    disableOnboardApproveButton ||
                    onboardingDocs.length === 0
                  }
                  style={{ margin: '0px 5px' }}
                  variant="contained"
                  sx={{
                    height: 40,
                    width: 90,
                    bgcolor: colors.greenAccent[500],
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                  onClick={() => {
                    setIsOnboardApproveOpen(true);
                  }}
                >
                  Approve
                </Button>
              </div>
            )}

            <Divider sx={{ marginBlock: 3 }} />
            <Typography
              component="h2"
              variant="h6"
              sx={{
                color: colors.greenAccent[500],
                fontWeight: 700,
                fontFamily: 'Inter !important',
              }}
            >
              {strings.displayText.fundingLimitCheckDeclarationSectionTitle}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}
            >
              {strings.displayText.DocumentsTab}
            </Typography>
            {fundingDocs.length > 0 ? (
              <DocumentList documents={fundingDocs} hideTitle />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {strings.displayText.noDocumentsUploaded}
              </Typography>
            )}
            {fundingNotesList.length > 0 ? (
              <NoteList
                notes={fundingNotesList}
                heading={strings.displayText.sectionNotesHeading}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {strings.displayText.noNotesYet}
              </Typography>
            )}

            {customerInfo && customerInfo.avcApprovalStatus !== 'Approved' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 16,
                }}
              >
                <Button
                  variant="contained"
                  style={{ margin: '0px 5px' }}
                  sx={{
                    height: 40,
                    width: 150,
                    bgcolor: colors.greenAccent[500],
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                  onClick={() => {
                    setIsAvcRejectOpen(true);
                  }}
                >
                  {strings.displayText.rejectAvc}
                </Button>
                <Button
                  disabled={
                    customerInfo?.avcApprovalStatus === 'Rejected' ||
                    disableAvcApproveButton ||
                    fundingDocs.length === 0
                  }
                  style={{ margin: '0px 5px' }}
                  variant="contained"
                  sx={{
                    height: 40,
                    width: 150,
                    bgcolor: colors.greenAccent[500],
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                  onClick={() => {
                    setIsAvcApproveOpen(true);
                  }}
                >
                  {strings.displayText.approveAvc}
                </Button>
              </div>
            )}

            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName="eSign"
              customerDetails={
                customerInfo?.clientDeclarationClientSignature ??
                customerInfo?.clientSignature ??
                '-'
              }
            />
            <ReuseGrid
              customName="Declaration Date"
              customerDetails={
                customerInfo?.clientDeclarationDateTime
                  ? dateFormat(customerInfo?.clientDeclarationDateTime)
                  : '-'
              }
            />
          </Grid>
        </Grid>
      </Paper>
      <DialogBox
        className=""
        open={isOnboardRejectOpen}
        onClose={handleCloseOnboardReject}
        title={strings.displayText.onboardingRejectionReasonTitle}
        fullWidth={true}
      >
        <div>
          <TextField
            multiline
            rows={4}
            style={{ width: '100%' }}
            value={onboardRejectNote}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setOnboardRejectNote(e.target.value);
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              paddingTop: '15px',
            }}
          >
            <Button
              variant="contained"
              style={{ margin: '0px 5px' }}
              sx={{
                height: 40,
                bgcolor: colors.greenAccent[500],
                borderRadius: 2,
                textTransform: 'none',
              }}
              onClick={handleCloseOnboardReject}
            >
              {strings.displayText.cancel}
            </Button>
            <Button
              disabled={onboardRejectNote === '' || disableOnboardRejectButton}
              style={{ margin: '0px 5px' }}
              variant="contained"
              sx={{
                height: 40,
                bgcolor: colors.greenAccent[500],
                borderRadius: 2,
                textTransform: 'none',
              }}
              onClick={handleOnboardReject}
            >
              Reject
            </Button>
          </div>
        </div>
      </DialogBox>

      <DialogBox
        className=""
        open={isAvcRejectOpen}
        onClose={handleCloseAvcReject}
        title={strings.displayText.avcRejectionReasonTitle}
        fullWidth={true}
      >
        <div>
          <TextField
            multiline
            rows={4}
            style={{ width: '100%' }}
            value={avcRejectNote}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setAvcRejectNote(e.target.value);
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              paddingTop: '15px',
            }}
          >
            <Button
              variant="contained"
              style={{ margin: '0px 5px' }}
              sx={{
                height: 40,
                bgcolor: colors.greenAccent[500],
                borderRadius: 2,
                textTransform: 'none',
              }}
              onClick={handleCloseAvcReject}
            >
              {strings.displayText.cancel}
            </Button>
            <Button
              disabled={avcRejectNote === '' || disableAvcRejectButton}
              style={{ margin: '0px 5px' }}
              variant="contained"
              sx={{
                height: 40,
                bgcolor: colors.greenAccent[500],
                borderRadius: 2,
                textTransform: 'none',
              }}
              onClick={handleAvcReject}
            >
              Reject
            </Button>
          </div>
        </div>
      </DialogBox>

      <AlertModal
        open={isOnboardApproveOpen}
        onClose={() => {
          setIsOnboardApproveOpen(false);
        }}
        title={`${strings.displayText.approveConfirmationText}`}
        isButtonForConfirmation={true}
        handleConfirm={handleOnboardApprove}
      />

      <AlertModal
        open={isAvcApproveOpen}
        onClose={() => {
          setIsAvcApproveOpen(false);
        }}
        title={`${strings.displayText.approveConfirmationText}`}
        isButtonForConfirmation={true}
        handleConfirm={handleAvcApprove}
      />
    </Box>
  );
};

export default UserDetails;
